import { Skeleton } from '@/components/ui/skeleton'
import { Frame } from '@/components/ui/frame'

// The roster is unknown until the query lands, so the matrix's counts are a guess; its BOX is not — the card,
// its column widths and the legend under it are the real ones, so nothing moves when the counts arrive.
const MATRIX_COLUMNS = 5
const MATRIX_ROWS = 4
const COLUMN_KEYS = Array.from({ length: MATRIX_COLUMNS }, (_, i) => `evaluation-column-${i}`)
const ROW_KEYS = Array.from({ length: MATRIX_ROWS }, (_, i) => `evaluation-row-${i}`)
// 180px evaluator column + 100px progress column + one 56px column per roster member (see the real table).
const MATRIX_WIDTH = 180 + 100 + MATRIX_COLUMNS * 56

/** The N×N evaluation matrix: a header row of evaluated members over one row per evaluator. */
export function TeamEvaluationPending() {
  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-7xl flex-col gap-4 px-4 pt-8 pb-4 @sm:px-6 @lg:px-8">
      <div className="flex shrink-0 items-center gap-3">
        <Skeleton className="size-10 shrink-0 rounded-xl" />
        <Skeleton className="h-6 w-44" />
      </div>

      <div className="bg-card min-h-0 w-full flex-1 overflow-hidden rounded-xl border pr-3 pb-3">
        <div className="mx-auto" style={{ width: MATRIX_WIDTH }}>
          <div className="flex items-end">
            <div className="w-[180px] py-3 pr-3 pl-4">
              <Skeleton className="h-3 w-20" />
            </div>
            {COLUMN_KEYS.map((key) => (
              <div key={key} className="flex w-14 flex-col items-center gap-1.5 py-2">
                <Skeleton className="size-9 rounded-md" />
                <Skeleton className="h-2.5 w-10" />
              </div>
            ))}
            <div className="w-[100px] p-2">
              <Skeleton className="mx-auto h-3 w-14" />
            </div>
          </div>

          <div className="divide-border divide-y">
            {ROW_KEYS.map((key) => (
              <div key={key} className="flex items-center">
                <div className="flex w-[180px] items-center gap-3 py-2 pr-2 pl-4">
                  <Skeleton className="size-8 shrink-0 rounded-md" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex h-5 items-center">
                      <Skeleton className="h-3.5 w-24" />
                    </div>
                    <div className="flex h-4 items-center">
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                </div>
                {COLUMN_KEYS.map((cellKey) => (
                  <div key={cellKey} className="flex w-14 justify-center py-1">
                    <Skeleton className="size-12 rounded-md" />
                  </div>
                ))}
                <div className="flex w-[100px] items-center gap-2 p-2">
                  <Skeleton className="h-1.5 flex-1 rounded-full" />
                  <Skeleton className="h-3 w-7" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-4">
        {['completed', 'pending', 'not-applicable'].map((key) => (
          <div key={key} className="flex items-center gap-2">
            <Skeleton className="size-5 shrink-0 rounded-md" />
            <div className="flex h-5 items-center">
              <Skeleton className="h-3.5 w-20" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** The per-member evaluation wizard: the progress header over the stepped form card. */
export function TeamEvaluatePending() {
  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 shrink-0 rounded-xl" />
          <Skeleton className="h-6 w-56" />
        </div>
        <div className="flex h-5 items-center">
          <Skeleton className="h-3.5 w-full max-w-96" />
        </div>
        <div className="flex items-center gap-3 py-1">
          <Skeleton className="h-1.5 w-48 shrink-0 rounded-full" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>

      <Frame className="flex flex-col gap-4">
        <div className="flex flex-col gap-5 px-4 pt-5 pb-5 sm:px-6 sm:pt-6">
          <Skeleton className="h-8 w-60 rounded-full sm:w-72" />
          <div className="flex min-w-0 flex-col gap-1">
            <Skeleton className="h-6 w-72 max-w-full" />
            <div className="flex h-5 items-center">
              <Skeleton className="h-3.5 w-32" />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex h-5 items-center">
              <Skeleton className="h-3.5 w-full" />
            </div>
            <div className="flex h-5 items-center">
              <Skeleton className="h-3.5 w-4/5" />
            </div>
          </div>
        </div>

        <div className="px-4 pb-4 sm:px-6">
          <Skeleton className="h-72 w-full rounded-xl" />
        </div>

        <div className="flex items-center justify-between px-4 sm:px-6">
          <Skeleton className="h-9 w-24 rounded-lg" />
          <Skeleton className="h-9 w-28 rounded-lg" />
        </div>
      </Frame>
    </div>
  )
}
